import React, { useMemo } from "react";
import { DashboardGraphViewType } from "../../../../constants/types";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type Props = {
  viewType: DashboardGraphViewType;
  onViewTypeChange: (viewType: DashboardGraphViewType) => void;
  year: number;
  onYearChange: (year: number) => void;
  month: number;
  onMonthChange: (month: number) => void;
};

export const ChartDateFilter: React.FC<Props> = ({
  viewType,
  onViewTypeChange,
  year,
  onYearChange,
  month,
  onMonthChange,
}) => {
  const currentYear = new Date().getFullYear();

  const yearOptions = useMemo(() => {
    const startYear = Math.min(year - 3, currentYear - 10);
    const endYear = Math.max(year + 2, currentYear + 2);
    const years: number[] = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push(y);
    }
    return years;
  }, [year, currentYear]);

  const handlePrevYear = () => {
    onYearChange(year - 1);
  };

  const handleNextYear = () => {
    onYearChange(year + 1);
  };

  const handlePrevMonth = () => {
    if (month === 1) {
      onMonthChange(12);
      onYearChange(year - 1);
    } else {
      onMonthChange(month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 12) {
      onMonthChange(1);
      onYearChange(year + 1);
    } else {
      onMonthChange(month + 1);
    }
  };

  return (
    <div className="d-flex align-items-center gap-2 flex-wrap ms-md-auto">
      {/* View Type Toggle Pills */}
      <div className="dashboard-chart-filter-group">
        <button
          type="button"
          className={`dashboard-chart-filter-btn ${
            viewType === DashboardGraphViewType.Monthly ? "active" : ""
          }`}
          onClick={() => onViewTypeChange(DashboardGraphViewType.Monthly)}
        >
          Monthly
        </button>
        <button
          type="button"
          className={`dashboard-chart-filter-btn ${
            viewType === DashboardGraphViewType.Weekly ? "active" : ""
          }`}
          onClick={() => onViewTypeChange(DashboardGraphViewType.Weekly)}
        >
          Weekly
        </button>
        <button
          type="button"
          className={`dashboard-chart-filter-btn ${
            viewType === DashboardGraphViewType.Daily ? "active" : ""
          }`}
          onClick={() => onViewTypeChange(DashboardGraphViewType.Daily)}
        >
          Daily
        </button>
      </div>

      {/* Month Stepper - only shown for Daily view */}
      {viewType === DashboardGraphViewType.Daily && (
        <div className="dashboard-chart-stepper" title="Select month">
          <button
            type="button"
            className="dashboard-chart-stepper__btn"
            onClick={handlePrevMonth}
            aria-label="Previous month"
          >
            ‹
          </button>
          <div className="dashboard-chart-stepper__select-wrapper">
            <select
              className="dashboard-chart-stepper__select"
              value={month}
              onChange={(e) => onMonthChange(Number(e.target.value))}
              aria-label="Select month"
            >
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="dashboard-chart-stepper__btn"
            onClick={handleNextMonth}
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      )}

      {/* Dynamic Year Stepper */}
      <div className="dashboard-chart-stepper" title="Select year">
        <button
          type="button"
          className="dashboard-chart-stepper__btn"
          onClick={handlePrevYear}
          aria-label="Previous year"
        >
          ‹
        </button>
        <div className="dashboard-chart-stepper__select-wrapper">
          <select
            className="dashboard-chart-stepper__select"
            value={year}
            onChange={(e) => onYearChange(Number(e.target.value))}
            aria-label="Select year"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="dashboard-chart-stepper__btn"
          onClick={handleNextYear}
          aria-label="Next year"
        >
          ›
        </button>
      </div>
    </div>
  );
};
